/**
 * Public User Enrollment: identity + relationship only. No tenant and no economics are created here —
 * MSC approves, and the enrollment profile decides whether a tenant is created.
 */
import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { btnPrimary, errorText, inputCls } from '../components/finance/financeUi';

const RELATIONSHIPS = [
  ['msc', 'I work with Merchant Solutions Corp'],
  ['iso_owner', 'I own or manage an ISO / sales organization'],
  ['solo_agent', "I'm an independent / solo agent"],
  ['agent_team', 'I run an independent agent team / agency'],
  ['organization', "We're an external organization / ISV"],
];
const NEEDS_ORG = ['iso_owner', 'agent_team', 'organization'];
const Field = ({ label, children, span }) => (
  <label className={`block text-sm ${span ? 'sm:col-span-2' : ''}`}>
    <span className="mb-1 block text-xs font-medium text-muted-foreground">{label}</span>{children}
  </label>
);

export default function SignupPage() {
  const [params] = useSearchParams();
  const inviteToken = params.get('invite');
  const [invite, setInvite] = useState(null);
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', mobile: '', password: '', relationship: 'msc', organization_name: '' });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  useEffect(() => {
    if (!inviteToken) return;
    api.request(`/enrollment/invite/${inviteToken}`).then((inv) => {
      setInvite(inv); setForm((f) => ({ ...f, email: inv.email || '', first_name: inv.first_name || '', last_name: inv.last_name || '' }));
    }).catch((e) => toast.error(errorText(e, 'Invitation not valid')));
  }, [inviteToken]);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      setDone(await api.request('/enrollment/signup', { method: 'POST', body: JSON.stringify({
        ...form, relationship: invite ? null : form.relationship, invite_token: invite ? inviteToken : null,
        organization_name: form.organization_name || null }) }));
    } catch (err) { toast.error(errorText(err, 'Signup failed')); } finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen bg-muted/30 px-4 py-12" data-testid="signup-page">
      <div className="mx-auto max-w-2xl">
        <p className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">ISOCNTRL</p>
        <h1 className="mt-1 text-4xl sm:text-5xl font-semibold tracking-tight">Create your ISOCNTRL account</h1>
        <p className="mt-2 text-sm text-muted-foreground">MSC reviews every request. Pricing, compensation and setup are configured after approval.</p>
        {done ? (
          <div className="mt-8 rounded-xl border bg-card p-8" data-testid="signup-success">
            <CheckCircle2 className="h-8 w-8 text-emerald-600" />
            <h2 className="mt-3 text-lg font-medium">Request received — pending review</h2>
            <p className="mt-1 text-sm text-muted-foreground">{done.message}</p>
            <Link to="/login" className="mt-5 inline-block text-sm font-medium underline" data-testid="signup-back-to-login">Back to sign in</Link>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8 grid gap-4 rounded-xl border bg-card p-6 sm:grid-cols-2" data-testid="signup-form">
            {invite && <p className="sm:col-span-2 rounded-lg bg-muted px-3 py-2 text-sm" data-testid="signup-invite-banner">Invited by <b>{invite.upline_name}</b>{invite.organization ? ` · ${invite.organization}` : ''} as {String(invite.proposed_role).replace('iso_', '')}</p>}
            <Field label="First name"><input required className={inputCls} value={form.first_name} onChange={set('first_name')} data-testid="signup-first-name" /></Field>
            <Field label="Last name"><input required className={inputCls} value={form.last_name} onChange={set('last_name')} data-testid="signup-last-name" /></Field>
            <Field label="Email"><input required type="email" readOnly={!!invite} className={inputCls} value={form.email} onChange={set('email')} data-testid="signup-email" /></Field>
            <Field label="Mobile"><input required className={inputCls} value={form.mobile} onChange={set('mobile')} data-testid="signup-mobile" /></Field>
            {!invite && (
              <Field label="How will you use ISOCNTRL?" span>
                <div className="grid gap-2" data-testid="signup-relationship">
                  {RELATIONSHIPS.map(([v, l]) => (
                    <label key={v} className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 transition-colors ${form.relationship === v ? 'border-primary bg-primary/5' : 'hover:bg-muted'}`}>
                      <input type="radio" name="relationship" value={v} checked={form.relationship === v} onChange={set('relationship')} data-testid={`signup-relationship-${v}`} />{l}
                    </label>
                  ))}
                </div>
              </Field>
            )}
            {!invite && NEEDS_ORG.includes(form.relationship) && (
              <Field label="Organization name" span><input required className={inputCls} value={form.organization_name} onChange={set('organization_name')} data-testid="signup-org-name" /></Field>
            )}
            <Field label="Password (8+ characters with an uppercase letter, a lowercase letter and a number)" span>
              <input required type="password" className={inputCls} value={form.password} onChange={set('password')} data-testid="signup-password" />
            </Field>
            <div className="flex items-center justify-between gap-3 sm:col-span-2">
              <Link to="/login" className="text-sm text-muted-foreground hover:underline" data-testid="signup-login-link">Already have an account? Sign in</Link>
              <button type="submit" className={btnPrimary} disabled={busy} data-testid="signup-submit-btn">{busy ? 'Submitting…' : 'Create account'}</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
